// "Adaugă mișcare de stoc" and "Modifică mișcarea" (spec 054 User Stories 1, 3
// and 4; FR-001 to FR-016, FR-020, FR-025). The wireframe's committed direction
// C composes a row in a dialog (wireframes/react/src/pages/raport-nou/VariantaC.tsx,
// piese.tsx `Cautare`, `CampLot`, `FormularRand`, `DupBox`): step A is one
// search over medicines and the lots already reported at the location; step B
// is the row, with the new stock level, the previous stock beside it, the
// signed difference and a reason that fits its sign (caiet UC50 step 7,
// research R1).
//
// Departures, each forced by AGE or MUD (research R12): `MudModal size="lg"`,
// 720 px, where the wireframe draws 760 — AGE's widest, and a knowing departure
// from MUD's single 588 desktop modal (owner request 2026-09-15, superseding
// R12's `md`); the search and the lot list are the
// shared DialogCombobox, whose panel stays inside the modal's top layer (the
// search follows MUD's search pattern, decision D4 and T106: AGE's Large
// search input, its results a 276 px menu in the dialog body under it); the
// wireframe's inactive-medicine toast is an inline message, because a toast
// paints under the dialog. Not copied from the wireframe: editing removed the
// row from the draft until it was saved again (FR-025), and an imported row's
// errors were put on fields by matching their text (they go by `field` here).
//
// With no location chosen yet (owner decision, 2026-09-15) the dialog still
// adds. The search and the lot list answer from the lots the Registrar has
// reported anywhere, without stock (owner request 2026-09-15). A lot it has
// never reported is new at any location, so its previous stock is 0 and the
// difference and the reasons follow; one it knows waits for the location
// rather than reading as 0 (constitution art. IV). The server reads the row
// again once the report is bound to a location.
//
// T109 (owner request 2026-09-15): the medicine's two statuses, authorisation
// and state, always both, beside its name; the medicine as the wireframe's
// summary (RezumatMed) with no box, and without DCI (owner decision
// 2026-09-15, a recorded departure); the form grouped by meaning per MUD's form
// pattern (mud/02-layouts-patterns.md §4-5): Lot, then Cantitate with its
// previous level below and difference at the trailing edge, then Motiv with
// Comentariu.
//
// "Corectează mișcarea" (User Story 6, FR-031, research R9) is the same dialog
// on a confirmed movement: its medicine, lot, expiry and the reason Corectare
// are locked, the quantity is "Cantitatea corectă" beside "Raportat: X", and a
// movement past the correction window is refused before anything is added.
//
// The dialog hands back one DraftRow. Creating or patching the draft, the toast
// and where the focus goes afterwards are the page's.
import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ElementRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslate } from '@tolgee/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  MudButton,
  MudCheckbox,
  MudIcon,
  MudInlineMessage,
  MudTextInput,
  MudModal,
  MudInfoBox,
  MudNumericInput,
  MudSelect,
  MudSeparator,
  MudTag,
  MudTextarea,
  MudTooltip,
} from '@egov-moldova/mud-react';
import {
  stockMovementReasonSchema,
  type MovementDirection,
  type RegistrarKind,
  type StockMovementReason,
} from '@simsm/shared-types';
import {
  DialogCombobox,
  EMPTY_CELL,
  ExpiryMonthInput,
  formatDate,
  formatExpiry,
  formatNumber,
  highlightMatch,
  MetadataLine,
  onModalDismiss,
  searchTerms,
} from '@simsm/ui-shared';
import { isNormalizedApiError } from '../../../app/api-client';
import type { DraftRow, MovementForCorrection } from '../../../features/reporting/api';
import {
  CorrectionTargetLine,
  useCorrectionTarget,
} from '../../../features/reporting/correction-target';
import { describeAuthorization, todayIso } from '../../../features/reporting/medicine-api';
import {
  authorisationStatus,
  medicineStateStatus,
  medicineSupportingLine,
  medicineTitle,
  useMedicineIdentity,
  type StatusTagSpec,
} from '../../../features/reporting/medicine-identity';
import {
  commentRequiredFor,
  difference,
  expiryDiffers,
  firstInvalidField,
  movementWarnings,
  reasonForSign,
  reasonLabelKey,
  reasonsFor,
  signOf,
  validateMovement,
  type DefaultReasons,
  type FieldMessage,
  type MovementField,
  type MovementFormInput,
  type MovementSign,
} from '../../../features/reporting/movement-form';
import {
  listLots,
  REPORT_SEARCH_MIN_LENGTH,
  searchReport,
  type MatchedLot,
  type ReportedLot,
  type ReportSearchHit,
} from '../../../features/reporting/report-search-api';
import { rowFieldMessages } from '../../../features/reporting/row-messages';
import type { RegistrarKindState } from '../../../features/reporting/use-locations';
import './expiry-month.css';
import './stock-movement-dialog.css';

/** What an add or an edit hands to the page: the row, and what the toast and the defaults need. */
export interface MovementAdded {
  row: DraftRow;
  medicineLabel: string;
  lot: string;
  reason: StockMovementReason;
  sign: MovementSign;
}

export interface StockMovementDialogProps {
  open: boolean;
  /**
   * The report's location; the previous stock and the lot list are per location
   * (R2). Null while none is chosen: the search is the catalogue's alone, and
   * the previous stock is not known yet (owner decision, 2026-09-15).
   */
  siteId: string | null;
  /** The report's rows: a lot this report already moved chains from its last level (R1). */
  rows: readonly DraftRow[];
  /**
   * The reason each direction starts from (FR-007), the active Registrar's type's.
   * Null until that type is known: the dialog then holds no reason.
   */
  defaults: DefaultReasons | null;
  /**
   * The direction a new movement starts from before its level is typed, set in
   * "Motive implicite" (caiet UC50 step 2): its default reason is the one the
   * dialog opens with. Absent is decrease (docs/shape-ops-default-reasons.md
   * §Decision 3).
   */
  defaultDirection?: MovementDirection | undefined;
  /**
   * The active Registrar's type (UC50-15, CF-15): the reason list holds its reasons
   * only, and a row stored with another is refused as core refuses it. Null until
   * it is known; no reason is offered without it
   * (docs/shape-ops-registrar-kind-required.md §Decision 6).
   */
  registrarKind?: RegistrarKind | null | undefined;
  /**
   * Whether `registrarKind` is known yet (useReportingLocations). Until it is
   * `ready` the dialog offers no reason and adds nothing, and on `error` it says
   * the allowed reasons cannot be read: a missing answer is not a type with no
   * table (docs/shape-ops-contract-codes.md §Decision 8). A correction is not held
   * back, its reason being Corectare for every type. Absent is `ready`.
   */
  registrarKindState?: RegistrarKindState | undefined;
  /** The row "Editează" opened: the dialog is "Modifică mișcarea". Absent when adding. */
  editing?: DraftRow | undefined;
  /**
   * A row to add again as it was entered: an edit a concurrent save could not
   * re-apply because its row was removed elsewhere (research R7). Add mode.
   */
  prefill?: DraftRow | undefined;
  /** The confirmed movement "Corectează mișcarea" names: the dialog is in correction mode. */
  correction?: MovementForCorrection | undefined;
  /** The new row, or in edit mode the edited row under its own clientRowId. */
  onAdd: (added: MovementAdded) => void;
  /** "Editează rândul existent": the row the duplicate repeats, opened in its place. */
  onEditRow?: ((clientRowId: string) => void) | undefined;
  onClose: () => void;
}

type TFn = ReturnType<typeof useTranslate>['t'];
type FieldElement = ElementRef<typeof MudTextInput>;
// The combobox field is a MudTextInput since MUD 1.2 (DialogCombobox, 059 T015).
type SearchFieldElement = FieldElement;

/**
 * One row of the search's list (T106): a medicine, or one of the lots of it
 * the query found, which picks the medicine and that lot together.
 */
type SearchOption =
  | { kind: 'medicine'; hit: ReportSearchHit }
  | { kind: 'lot'; hit: ReportSearchHit; lot: MatchedLot };

/** The lots a hit's row lists; the bff sends at most this many. */
const MATCHED_LOTS_SHOWN = 3;

const SEARCH_DEBOUNCE_MS = 150;
/** About half a second of frames in which an opening dialog keeps its cursor where it put it. */
const FOCUS_FRAMES = 30;

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

// An AGE field takes focus on its inner control when its shadow root is open;
// the host otherwise, which is what `combobox-a11y` focuses as well.
function focusControl(element: unknown): void {
  if (!(element instanceof HTMLElement)) return;
  const inner = element.shadowRoot?.querySelector<HTMLElement>('input, textarea, select, button');
  (inner ?? element).focus();
}

function hasFocus(element: HTMLElement): boolean {
  const active = document.activeElement;
  return active === element || (active !== null && element.contains(active));
}

// Focus nobody chose: the page behind the dialog, or the modal's own chrome,
// whose close button sits in its shadow root (so the document reports the host).
function focusIsIdle(root: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (active === null || active === document.body) return true;
  const modal = root?.closest('mud-modal') ?? root;
  if (!modal) return true;
  return active === modal || !modal.contains(active);
}

function nextFrame(callback: () => void): number {
  if (typeof window.requestAnimationFrame === 'function')
    return window.requestAnimationFrame(callback);
  return window.setTimeout(callback, 16);
}

function cancelFrame(handle: number): void {
  if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(handle);
  else window.clearTimeout(handle);
}

/**
 * Puts the cursor in a field now, and keeps it there for a moment while nothing
 * else has it. Without this the first keystrokes after N were lost: an AGE field
 * renders its inner control a frame after React creates it, and `mud-modal`'s
 * showModal() then moves focus to its own chrome. A click stops it at once.
 */
function focusSoon(target: () => unknown, root: () => HTMLElement | null): () => void {
  let frames = FOCUS_FRAMES;
  let handle = 0;
  let stopped = false;
  const stop = (): void => {
    stopped = true;
    document.removeEventListener('pointerdown', stop, true);
  };
  document.addEventListener('pointerdown', stop, true);
  const attempt = (): void => {
    if (stopped) return;
    const element = target();
    if (
      element instanceof HTMLElement &&
      !hasFocus(element) &&
      (frames === FOCUS_FRAMES || focusIsIdle(root()))
    ) {
      focusControl(element);
    }
    frames -= 1;
    if (frames > 0) handle = nextFrame(attempt);
    else stop();
  };
  attempt();
  return () => {
    stop();
    cancelFrame(handle);
  };
}

// The inner control takes a routed value at once, so a key that lands there
// natively next continues from it rather than from the value of a frame ago.
function writeThrough(host: { readonly shadowRoot: ShadowRoot | null }, value: string): void {
  const inner = host.shadowRoot?.querySelector('input');
  if (inner) inner.value = value;
}

function newClientRowId(): string {
  return `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** A stored reason code; '' for none or an unknown code. */
function reasonOf(code: string | undefined): StockMovementReason | '' {
  const parsed = stockMovementReasonSchema.safeParse(code);
  return parsed.success ? parsed.data : '';
}

/**
 * What the expiry field starts from: the row's own value as it is. A `YYYY-MM` stays a
 * month, so confirming stores it as reported, with the `MONTH` precision (UC50-02,
 * docs/shape-ops-lot-expiry-precision.md D6).
 */
function expiryOf(row: DraftRow | undefined): string {
  return row?.lotExpiry ?? '';
}

/** The movement a correction targets, as the fields start from it (R9). */
function rowFromMovement(movement: MovementForCorrection): DraftRow {
  return {
    clientRowId: '',
    medicineCode: movement.medicineCode,
    medicineLabel: movement.medicineLabel,
    lot: movement.lot,
    lotExpiry: movement.lotExpiry,
    reason: 'CORRECTION',
    lotMatch: 'EXISTING',
    validationErrors: [],
  };
}

const CORRECTION_REASON: StockMovementReason = 'CORRECTION';

function withoutComment(row: DraftRow): DraftRow {
  const copy = { ...row };
  delete copy.comment;
  return copy;
}

// The row without the messages of one code, which the dialog already answers.
function withoutIssue(row: DraftRow, code: string): DraftRow {
  return {
    ...row,
    validationErrors: row.validationErrors.filter((issue) => issue.code !== code),
  };
}

/** The medicine of a row being edited, as the identity panel reads it. */
function hitFromRow(row: DraftRow): ReportSearchHit {
  const code = row.medicineCode ?? '';
  const label = row.medicineLabel || code;
  return {
    code,
    medicineLabel: label,
    name: label,
    dci: null,
    form: null,
    dose: null,
    volume: null,
    division: null,
    producer: null,
    country: null,
    atc: null,
    barcode: null,
    authorised: true,
    authorisationExpiry: null,
    reportableUntil: null,
    active: true,
    matched: [],
    lots: { matched: [], matchedTotal: 0, reportedCount: 0 },
  };
}

// The lot list reads by expiry, soonest first (FR-010); a lot with none stated last.
function byExpiry(a: ReportedLot, b: ReportedLot): number {
  return (a.expiry ?? '9999').localeCompare(b.expiry ?? '9999') || a.lot.localeCompare(b.lot);
}

function fold(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function message(t: TFn, field: FieldMessage | undefined): string | undefined {
  if (!field) return undefined;
  if (field.reason) return t(field.key, { reason: t(reasonLabelKey(field.reason)) });
  return t(field.key);
}

// Every match of every typed word, in every field: the title, the code, the DCI,
// the producer (owner, 2026-09-15). A row must show where it matched, or it reads
// as matched for no reason. The double "ca" of T106 came from a title that said
// "capsule" twice; titles are said once now (medicineTitle), so marking every
// match no longer doubles a word.
function Highlighted({ text, terms }: { text: string; terms: readonly string[] }): JSX.Element {
  return (
    <>
      {highlightMatch(text, terms).map((segment, index) =>
        segment.match ? (
          <mark key={index}>{segment.text}</mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * The medicine's two statuses (T109, owner request 2026-09-15), always both:
 * where the code stands on the national lists, then its state in SIMSM. They
 * vary independently (a lapsed authorisation stays active through UC20-10's
 * grace period), so one tag each; the old single tag hid one of them.
 */
function statusesOf(hit: ReportSearchHit): readonly StatusTagSpec[] {
  return [authorisationStatus(hit, todayIso()), medicineStateStatus(hit)];
}

/** The two status tags, 8 px apart (MUD tag spacing); they never shrink, the name wraps. */
function StatusTags({ hit, className }: { hit: ReportSearchHit; className: string }): JSX.Element {
  const { t } = useTranslate();
  return (
    <span className={className}>
      {statusesOf(hit).map((status) => (
        <MudTag
          key={status.key}
          variant="status"
          size="sm"
          semantic={status.semantic}
          label={t(status.key)}
        />
      ))}
    </span>
  );
}

/** A search row's accessible name, in words (T106): what the row shows, minus the highlight's seams. */
function searchOptionLabel(t: TFn, option: SearchOption): string {
  if (option.kind === 'lot') {
    const { lot } = option;
    return `${lot.lot} · ${t('stock_reporting.dialog.search.lot_detail', {
      stock: formatNumber(lot.stock),
      expiry: lot.expiry ? formatExpiry(lot.expiry, lot.expiryPrecision) : EMPTY_CELL,
    })}`;
  }
  const { hit } = option;
  return [
    medicineTitle(hit),
    medicineSupportingLine(hit).join(' · '),
    hit.division ?? '',
    // The pill's count, in words: the pill splits it into a bold number and a
    // label, which a screen reader would otherwise read as two fragments. Before
    // the statuses, which end the name (T109).
    hit.lots.reportedCount > 0
      ? t('stock_reporting.dialog.search.reported_lots', { count: hit.lots.reportedCount })
      : '',
    ...statusesOf(hit).map((status) => t(status.key)),
  ]
    .filter((part) => part.length > 0)
    .join(', ');
}

function AuthorisationNote({
  hit,
  className,
}: {
  hit: ReportSearchHit;
  className: string;
}): JSX.Element | null {
  const { t } = useTranslate();
  // caiet UC50 step 4: the authorisation expiry of an authorised code.
  const note = describeAuthorization(
    { authorized: hit.authorised, authorizationExpiresOn: hit.authorisationExpiry ?? '' },
    todayIso(),
  );
  if (!note) return null;
  // Lapsed but still active: inside UC20-10's grace period, which the bff
  // counts from the configured setting. The row names the last day it holds.
  if (note.expired && hit.active && hit.reportableUntil) {
    return (
      <span className={`${className} ${className}--expired`}>
        {t('stock_reporting.dialog.search.authorisation_grace', {
          date: formatDate(note.iso),
          until: formatDate(hit.reportableUntil),
        })}
      </span>
    );
  }
  return (
    <span className={note.expired ? `${className} ${className}--expired` : className}>
      {t(note.key, { date: formatDate(note.iso) })}
    </span>
  );
}

/**
 * A medicine in the search list, as MUD's selection-menu row with supporting
 * text (T106): the title (brand, dose, form, said once) with the medicine's two
 * statuses beside it (T109), over code · DCI · producer; on the right the pack
 * size, over the authorisation expiry the caiet asks every result to carry
 * (UC50 step 4). Country and ATC are the summary's, once it is chosen.
 */
function SearchHitOption({
  hit,
  terms,
}: {
  hit: ReportSearchHit;
  terms: readonly string[];
}): JSX.Element {
  const { t } = useTranslate();
  const supporting = medicineSupportingLine(hit);
  return (
    <span className="simsm-movement-hit">
      <span className="simsm-movement-hit__main">
        <span className="simsm-movement-hit__title-line">
          <span className="simsm-movement-hit__title">
            <Highlighted text={medicineTitle(hit)} terms={terms} />
          </span>
          <StatusTags hit={hit} className="simsm-movement-hit__status" />
        </span>
        {supporting.length > 0 ? (
          <span className="simsm-movement-hit__support">
            {supporting.map((part, index) => (
              <Fragment key={index}>
                {index > 0 ? ' · ' : null}
                <Highlighted text={part} terms={terms} />
              </Fragment>
            ))}
          </span>
        ) : null}
        {/* The wireframe's "1 lot raportat anterior": the lots the Registrar
            has reported of this medicine — at this location, or with none
            chosen yet at any of its locations (owner request 2026-09-15). */}
        {hit.lots.reportedCount > 0 ? (
          <span className="simsm-movement-hit__lots">
            {/* The wireframe's `.rez-lot.rez-lot-cnt` (piese.tsx:304): a quiet
                pill, the number in bold, the words after it. Not a control —
                the lots themselves are chosen in the Număr lot field. */}
            <span className="simsm-movement-hit__lot-count">
              <strong>{formatNumber(hit.lots.reportedCount)}</strong>
              {t('stock_reporting.dialog.search.reported_lots_label', {
                count: hit.lots.reportedCount,
              })}
            </span>
          </span>
        ) : null}
      </span>
      <span className="simsm-movement-hit__side">
        {hit.division ? (
          <span className="simsm-movement-hit__tags">
            <MudTag variant="status" size="sm" semantic="muted" label={hit.division} />
          </span>
        ) : null}
        <AuthorisationNote hit={hit} className="simsm-movement-hit__auth" />
      </span>
    </span>
  );
}

/**
 * The bff's refusal of a query under 2 characters. The field sends none, so
 * one that comes back is no search at all rather than a failure (T107).
 */
function isQueryTooShort(error: unknown): boolean {
  if (isNormalizedApiError(error)) return error.code === 'QUERY_TOO_SHORT';
  const raw = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  return raw === 'QUERY_TOO_SHORT';
}

/**
 * Nothing found (T107, owner request 2026-09-15): MUD's compact empty state,
 * centred in the list, naming the query. With no location chosen the search
 * is the catalogue's alone, and the second hint says where the lots went.
 */
function SearchEmpty({ query, unbound }: { query: string; unbound: boolean }): JSX.Element {
  const { t } = useTranslate();
  return (
    <div className="simsm-movement-search-empty">
      <span className="simsm-movement-search-empty__icon" aria-hidden="true">
        <MudIcon name="search" size={24} />
      </span>
      <span className="simsm-movement-search-empty__title">
        {t('stock_reporting.dialog.search.empty.title', { query })}
      </span>
      <span className="simsm-movement-search-empty__hint">
        {t('stock_reporting.dialog.search.empty.hint')}
      </span>
      {unbound ? (
        <span className="simsm-movement-search-empty__hint">
          {t('stock_reporting.dialog.search.empty.lots_need_location')}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The search failed (T107): said as a search, never with the server's
 * sentence, which speaks of saving. "Reîncearcă" runs the same query again.
 */
function SearchFailed({ onRetry }: { onRetry: () => void }): JSX.Element {
  const { t } = useTranslate();
  return (
    <div className="simsm-movement-search-failed">
      <MudInlineMessage variant="error" size="medium">
        {t('stock_reporting.dialog.search.error')}
      </MudInlineMessage>
      <MudButton shape="circular" appearance="text" variant="strict" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </MudButton>
    </div>
  );
}

/** A lot of a hit the query found: "AB123 · stoc 40 · 05/2027", its number the row's title. */
function MatchedLotOption({
  lot,
  terms,
}: {
  lot: MatchedLot;
  terms: readonly string[];
}): JSX.Element {
  const { t } = useTranslate();
  return (
    <span className="simsm-movement-hit-lot">
      <span className="simsm-movement-hit-lot__number">
        <Highlighted text={lot.lot} terms={terms} />
      </span>
      {' · '}
      {t('stock_reporting.dialog.search.lot_detail', {
        stock: formatNumber(lot.stock),
        expiry: lot.expiry ? formatExpiry(lot.expiry, lot.expiryPrecision) : EMPTY_CELL,
      })}
    </span>
  );
}

// One reported lot: number, stock here, expiry, in the columns the header names
// (piese.tsx `CampLot`). A lot the Registrar reported only elsewhere is new
// here, with no stock at this location (research R2).
// With no location (`unbound`) every lot listed is one the Registrar reported
// somewhere: whether it is new here, and its stock, are the location's to say.
function LotOption({ lot, unbound = false }: { lot: ReportedLot; unbound?: boolean }): JSX.Element {
  const { t } = useTranslate();
  return (
    <span className="simsm-movement-lot__row">
      <span className="simsm-movement-lot__number">
        <strong>{lot.lot}</strong>
        {lot.reportedHere || unbound ? null : (
          <MudTag
            variant="status"
            size="sm"
            semantic="brand"
            label={t('stock_reporting.table.tag.new_lot')}
          />
        )}
      </span>
      <span className="simsm-movement-lot__num">
        {lot.reportedHere ? formatNumber(lot.stock) : EMPTY_CELL}
      </span>
      <span className="simsm-movement-lot__num">
        {lot.expiry ? formatExpiry(lot.expiry, lot.expiryPrecision) : EMPTY_CELL}
      </span>
    </span>
  );
}

/**
 * A field's label with the wireframe's (i) tip beside it (piese.tsx `InfoTip`;
 * spec 054 T100) and, for the lot, its tag at the end of the line. AGE draws a
 * label inside the control's shadow root, where nothing can sit beside it, so
 * the label is drawn here and the control is named by `ariaLabel`: the pattern
 * FormPage's `info` already uses. AGE hides its own label row, required mark
 * included, when it is given no label, so the mark is drawn here too.
 */
function FieldHead({
  label,
  tip,
  required = false,
  tag,
}: {
  label: string;
  tip?: string | undefined;
  required?: boolean;
  tag?: ReactNode;
}): JSX.Element {
  return (
    <span className="simsm-movement-dialog__field-head">
      <span className="simsm-movement-dialog__field-label">
        {label}
        {required ? (
          <span className="simsm-movement-dialog__field-req" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </span>
      {tip ? (
        <MudTooltip content={tip} position="top">
          <span
            slot="trigger"
            className="simsm-movement-dialog__field-info"
            role="img"
            aria-label={tip}
          >
            <MudIcon name="circle-info" variant="filled" size={16} />
          </span>
        </MudTooltip>
      ) : null}
      {tag ? <span className="simsm-movement-dialog__field-tag">{tag}</span> : null}
    </span>
  );
}

/**
 * The medicine, the form's first group, as the wireframe's `RezumatMed` sums it
 * up (piese.tsx; T100, T109): "Medicament" over the name, with the two statuses
 * on the name's line; then Formă · Doză · Volum · Divizare, then Cod ·
 * Producător · Țară, each labelled, a fact the catalogue lacks left out; then
 * the authorisation expiry. No box around it (owner request 2026-09-15): a
 * subtle MUD separator ends it, since without a fill the read-only summary and
 * the fields to fill in would otherwise run together. DCI is not repeated here
 * (owner decision 2026-09-15, a departure from RezumatMed); the search row says it.
 * "Schimbă medicamentul" is the group's own quiet action, at the end of its label row.
 */
function MedicineSummary({
  hit,
  known,
  action,
  error,
}: {
  hit: ReportSearchHit;
  /** False while a row's own medicine waits for the catalogue (T099): nothing to say of its statuses yet. */
  known: boolean;
  action: ReactNode;
  error: string | undefined;
}): JSX.Element {
  const { t } = useTranslate();
  const labelId = useId();
  return (
    <section className="simsm-movement-medicine" aria-labelledby={labelId}>
      <div className="simsm-movement-medicine__head">
        <span id={labelId} className="simsm-movement-dialog__field-label">
          {t('stock_reporting.dialog.medicine.label')}
        </span>
        {action}
      </div>
      <div className="simsm-movement-medicine__name-line">
        <span className="simsm-movement-medicine__name">{hit.name || hit.medicineLabel}</span>
        {known ? <StatusTags hit={hit} className="simsm-movement-medicine__status" /> : null}
      </div>
      <div className="simsm-movement-medicine__facts">
        <MetadataLine
          wrap
          items={[
            { label: t('stock_reporting.dialog.medicine.form'), value: hit.form ?? '' },
            { label: t('stock_reporting.dialog.medicine.dose'), value: hit.dose ?? '' },
            { label: t('stock_reporting.dialog.medicine.volume'), value: hit.volume ?? '' },
            { label: t('stock_reporting.dialog.medicine.division'), value: hit.division ?? '' },
          ]}
        />
        <MetadataLine
          wrap
          items={[
            { label: t('stock_reporting.dialog.medicine.code'), value: hit.code },
            {
              label: t('stock_reporting.dialog.medicine.producer'),
              value: hit.producer ?? '',
              title: hit.producer ?? undefined,
            },
            { label: t('stock_reporting.dialog.medicine.country'), value: hit.country ?? '' },
          ]}
        />
        {known ? <AuthorisationNote hit={hit} className="simsm-movement-medicine__auth" /> : null}
      </div>
      {error ? (
        <MudInlineMessage variant="error" size="small">
          {error}
        </MudInlineMessage>
      ) : null}
      <MudSeparator
        orientation="horizontal"
        variant="subtle"
        size="thin"
        className="simsm-movement-medicine__rule"
      />
    </section>
  );
}

export function StockMovementDialog({
  open,
  siteId,
  rows,
  defaults,
  defaultDirection = 'decrease',
  registrarKind,
  registrarKindState = 'ready',
  editing,
  prefill,
  correction,
  onAdd,
  onEditRow,
  onClose,
}: StockMovementDialogProps): JSX.Element {
  const { t } = useTranslate();
  // Correction mode: a movement named by "Corectează mișcarea", or a correction
  // row of this report opened again. Both lock what identifies the movement.
  const correctionRow = editing?.correctionOf ? editing : undefined;
  const correcting = correction !== undefined || correctionRow !== undefined;
  // UC91-16, CF-59: the corrected movement's report and channel; a correction
  // row opened again reads them. One sent through the API is corrected only
  // once its warning is confirmed (TC-UC91-16-06, TC-UC93-12-04).
  const targetRead = useCorrectionTarget(correction ? undefined : correctionRow?.correctionOf);
  const target = correction ?? targetRead.data;
  const targetPending =
    correctionRow !== undefined && correction === undefined && targetRead.isPending;
  const fromApi = correcting && target?.source === 'AUTOMATED';
  const [apiConfirmed, setApiConfirmed] = useState(false);
  const apiConfirmRef = useRef<ElementRef<typeof MudCheckbox> | null>(null);
  const reportedLevel = correction
    ? correction.reportedLevel
    : (correctionRow?.previousQuantity ?? 0);
  // A movement past the window cannot be corrected (UC50-06); core refuses it
  // too, on save, on read and at confirm.
  const windowClosed = correction !== undefined && !correction.withinWindow;
  const source = correction ? rowFromMovement(correction) : (editing ?? prefill);
  const editable = source?.medicineCode ? source : undefined;
  const editsRow = editable !== undefined;
  // The reasons of the active Registrar's type, once that type, and so the defaults
  // drawn from it, are known (§Decision 8).
  const reasonsKnown = correcting || (registrarKindState === 'ready' && defaults !== null);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ReportSearchHit | null>(() =>
    editable ? hitFromRow(editable) : null,
  );
  // The medicine of the edited row, not a search hit: nothing to say about its authorisation.
  const [pickedFromRow, setPickedFromRow] = useState(editable !== undefined);
  // An inactive medicine chosen by itself: refused, and said so under the search (UC50-01).
  const [refused, setRefused] = useState<ReportSearchHit | null>(null);
  const [lot, setLot] = useState(editable?.lot ?? '');
  const [lotExpiry, setLotExpiry] = useState(() => expiryOf(editable));
  // Chosen from the lot list (or found by its number in the search): lot and
  // expiry are that lot's and do not change (UC50-04, FR-010).
  const [lotChosen, setLotChosen] = useState(editable?.lotMatch === 'EXISTING');
  // "Lot nou" chosen: the list stays shut while the number is typed.
  const [lotNewChosen, setLotNewChosen] = useState(false);
  const [quantity, setQuantity] = useState<number | null>(
    typeof editable?.currentQuantity === 'number' ? editable.currentQuantity : null,
  );
  // A new row holds no reason until the type that decides it is known; then it
  // starts from the default direction's default.
  const [reason, setReason] = useState<StockMovementReason | ''>(() =>
    editable
      ? reasonOf(editable.reason)
      : reasonsKnown && defaults
        ? defaults[defaultDirection]
        : '',
  );
  // CF-59, BUG-1-020: a correction changes the quantity and nothing else, so its
  // comment is neither shown from the row nor sent.
  const [comment, setComment] = useState(correcting ? '' : (editable?.comment ?? ''));
  // Errors show from the first attempt to add, then follow every edit.
  const [submitted, setSubmitted] = useState(false);
  // The fields changed since the dialog opened: an edited row's own messages
  // stop speaking for a field once it is changed.
  const [touched, setTouched] = useState<ReadonlySet<MovementField>>(() => new Set());
  // Enter inside an open reason list chooses the reason; it must not add the row.
  const reasonListOpen = useRef(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<SearchFieldElement | null>(null);
  const lotRef = useRef<FieldElement | null>(null);
  const expiryRef = useRef<HTMLDivElement>(null);
  const quantityRef = useRef<ElementRef<typeof MudNumericInput> | null>(null);
  const reasonRef = useRef<ElementRef<typeof MudSelect> | null>(null);
  const commentRef = useRef<ElementRef<typeof MudTextarea> | null>(null);
  const changeRef = useRef<HTMLDivElement>(null);
  // What the search holds this instant, for keys routed faster than a render.
  const queryRef = useRef(query);
  queryRef.current = query;

  function touch(...fields: MovementField[]): void {
    setTouched((current) => {
      if (fields.every((field) => current.has(field))) return current;
      const next = new Set(current);
      fields.forEach((field) => next.add(field));
      return next;
    });
  }

  // A field chosen for the cursor (after a pick, a lot, a failed add) takes it
  // as soon as its AGE control has rendered: step B's fields are new, and a
  // single focus() a tick later could land before them, losing the keys typed
  // next (see `focusSoon`).
  const pendingFocus = useRef<(() => void) | null>(null);
  function focusFieldSoon(field: MovementField): void {
    pendingFocus.current?.();
    pendingFocus.current = focusSoon(
      () => fieldElement(field),
      () => rootRef.current,
    );
  }
  useEffect(() => () => pendingFocus.current?.(), []);

  // Step A opens with the cursor in the search (User Story 1, scenario 1), and
  // so does "Schimbă medicamentul".
  const stepA = picked === null;
  useEffect(() => {
    if (!open || !stepA) return undefined;
    return focusSoon(
      () => searchRef.current,
      () => rootRef.current,
    );
  }, [open, stepA]);

  // Keys typed before the search holds the cursor go into it, not nowhere.
  // Space on a button and Enter anywhere are the control's own; so is anything
  // typed in the result list, whose typeahead already writes to the search.
  useEffect(() => {
    if (!open || !stepA) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.isComposing) return;
      const search = searchRef.current;
      if (!(search instanceof HTMLElement)) return;
      const origin = event.composedPath()[0];
      if (origin instanceof Node) {
        if (origin === search || search.contains(origin) || search.shadowRoot?.contains(origin))
          return;
        if (searchBoxRef.current?.contains(origin)) return;
      }
      const onControl =
        origin instanceof Element && origin.closest('button, mud-button, textarea') !== null;
      let next: string;
      if (event.key === 'Backspace' && !onControl) next = queryRef.current.slice(0, -1);
      else if (event.key.length === 1 && !(event.key === ' ' && onControl))
        next = queryRef.current + event.key;
      else return;
      event.preventDefault();
      queryRef.current = next;
      setQuery(next);
      setRefused(null);
      writeThrough(search, next);
      focusControl(search);
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, stepA]);

  // ── Step A: the search ────────────────────────────────────────────────────
  const trimmed = query.trim();
  const settled = useDebounced(trimmed, SEARCH_DEBOUNCE_MS);
  const searchEnabled = stepA && settled.length >= REPORT_SEARCH_MIN_LENGTH;
  const search = useQuery({
    queryKey: ['stock-report-search', siteId, settled],
    // Each answer carries the query it answers (T107): kept in the list while
    // the next one runs, an empty answer still names its own query.
    queryFn: async () => ({ ...(await searchReport(settled, siteId)), query: settled }),
    enabled: searchEnabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const tooShort = search.isError && isQueryTooShort(search.error);
  const listing = searchEnabled && trimmed.length >= REPORT_SEARCH_MIN_LENGTH && !tooShort;
  // A failure with nothing to show. A background refetch that fails keeps the
  // rows it would have refreshed.
  const failed = search.isError && !tooShort && search.data === undefined;
  const hits = listing && !failed ? (search.data?.hits ?? []) : [];
  const terms = useMemo(() => searchTerms(settled), [settled]);
  // Each hit is an option; the lots of it the query found follow it as a group
  // of options under a section heading (T106, MUD menu `.section-heading`).
  const searchGroups = useMemo(
    () =>
      hits.flatMap((hit) => {
        const medicine = {
          key: `m:${hit.code}`,
          options: [{ kind: 'medicine', hit } as SearchOption],
        };
        const found = hit.lots.matched.slice(0, MATCHED_LOTS_SHOWN);
        if (found.length === 0) return [medicine];
        const count = Math.max(hit.lots.matchedTotal, found.length);
        return [
          medicine,
          {
            key: `l:${hit.code}`,
            label: t('stock_reporting.dialog.search.lots_group', {
              count,
              medicine: medicineTitle(hit),
            }),
            heading: t('stock_reporting.dialog.search.lots_heading', { count }),
            options: found.map((lot): SearchOption => ({ kind: 'lot', hit, lot })),
          },
        ];
      }),
    [hits, t],
  );

  // The same query again, with the cursor back where it is typed (T107).
  function retrySearch(): void {
    void search.refetch();
    focusControl(searchRef.current);
  }

  // The list's states (T107). While a query runs the spinner is in the field
  // and the list keeps the previous answer, rows or empty state, so it neither
  // blanks nor changes height between keystrokes. "Se caută…" is said only
  // before the first answer.
  let status: ReactNode;
  if (listing) {
    if (failed) status = <SearchFailed onRetry={retrySearch} />;
    else if (!search.data) status = t('stock_reporting.dialog.search.loading');
    else if (search.data.hits.length === 0)
      status = <SearchEmpty query={search.data.query} unbound={siteId === null} />;
  }
  const total = search.data?.total ?? 0;
  const footer =
    listing && total > hits.length
      ? t('stock_reporting.dialog.search.overflow', { shown: hits.length, total })
      : undefined;

  // ── Step B: the row ───────────────────────────────────────────────────────
  const code = picked?.code ?? '';
  const lots = useQuery({
    queryKey: ['stock-lots', siteId, code],
    queryFn: () => listLots(siteId, code),
    // A correction's lot is the movement's own; there is nothing to choose.
    // With no location the list is the Registrar's lots, known elsewhere and
    // with no stock here (owner request 2026-09-15): a lot not among them is new.
    enabled: code !== '' && !correcting,
    staleTime: 30_000,
  });
  const reportedLots = useMemo(() => [...(lots.data ?? [])].sort(byExpiry), [lots.data]);
  // T099: a row opened from the report carries only its code and the label it
  // was saved with, and an imported row not even that. The catalogue gives the
  // name and the identity a searched medicine is shown with.
  const resolved = useMedicineIdentity(siteId, code, pickedFromRow);
  const shownMedicine: ReportSearchHit | null =
    picked && pickedFromRow && resolved
      ? { ...resolved, matched: picked.matched, lots: picked.lots }
      : picked;

  // Rows before the edited one are what it chains from (R1); every other row
  // is what it may not repeat (DUPLICATE_ROW).
  const editIndex = editing ? rows.findIndex((row) => row.clientRowId === editing.clientRowId) : -1;
  const earlierRows = editIndex >= 0 ? rows.slice(0, editIndex) : rows;
  const otherRows = editing ? rows.filter((row) => row.clientRowId !== editing.clientRowId) : rows;

  const lotKey = lot.trim();
  // A lot the Registrar knows, here or elsewhere: its expiry is the lot's and
  // is filled in and locked (UC50-04, research R2).
  const knownLot = lotKey ? reportedLots.find((reported) => reported.lot === lotKey) : undefined;
  const expiryLocked = lotChosen || Boolean(knownLot?.expiry);
  const effectiveExpiry = expiryLocked && knownLot?.expiry ? knownLot.expiry : lotExpiry;
  // UC50-04: a number typed, not picked from the list, that the list holds is an
  // existing lot entered as new, and the dialog says what it took over. A month
  // typed before the lot (or saved on the row) that is not the lot's gives way
  // to the lot's, and that is said too, never done silently
  // (docs/shape-ops-existing-lot-alerts.md §Decision 3).
  const knownTyped = !correcting && !lotChosen && knownLot !== undefined;
  const replacedExpiry =
    !correcting && knownLot?.expiry && expiryDiffers(lotExpiry, knownLot.expiry)
      ? { entered: lotExpiry, known: knownLot.expiry, knownPrecision: knownLot.expiryPrecision }
      : null;

  // The previous stock (R1, R2): the server's own figure for the row being
  // edited, else the last level this report already gave the same medicine and
  // lot, else the lot's stock at this location, else 0 for a lot new here. The
  // server recomputes it on save and again at confirm. With no location it is
  // unknown, never 0 (owner decision, 2026-09-15; constitution art. IV).
  const unbound = siteId === null;
  let previous: number | null = null;
  let lotIsNew = true;
  if (correcting && picked && lotKey !== '') {
    // R9: a correction is measured against the level the movement reported,
    // and core records corrected − reported.
    previous = reportedLevel;
    lotIsNew = false;
  } else if (picked && lotKey !== '') {
    const chained = [...earlierRows]
      .reverse()
      .find(
        (row) =>
          row.medicineCode === picked.code &&
          (row.lot ?? '').trim() === lotKey &&
          typeof row.currentQuantity === 'number',
      );
    if (
      !unbound &&
      editing &&
      editing.medicineCode === picked.code &&
      (editing.lot ?? '').trim() === lotKey &&
      typeof editing.previousQuantity === 'number'
    ) {
      previous = editing.previousQuantity;
      lotIsNew = editing.lotMatch === 'NEW';
    } else if (chained) {
      previous = chained.currentQuantity ?? 0;
      lotIsNew = false;
    } else if (!unbound && !lots.isPending) {
      const here = reportedLots.find(
        (reported) => reported.lot === lotKey && reported.reportedHere,
      );
      previous = here ? here.stock : 0;
      lotIsNew = !here;
    } else if (unbound && lots.isSuccess) {
      // No location yet (owner request 2026-09-15): a lot the Registrar has
      // never reported anywhere is new at whichever location is chosen, so its
      // previous stock is 0. One it knows stays unknown until the location says.
      if (!reportedLots.some((reported) => reported.lot === lotKey)) {
        previous = 0;
        lotIsNew = true;
      }
    }
  }
  // Waiting for the location, not merely for a lot to be typed.
  const previousPending = unbound && previous === null;
  const diff = quantity !== null && previous !== null ? difference(quantity, previous) : null;
  const sign = signOf(diff);

  // FR-007: a reason that no longer fits the new sign falls back to that
  // direction's default; one that still fits is kept.
  // A correction's reason is Corectare whatever the sign. A new row opened before
  // the type was known starts, once it is, from that type's default (§Decision 8).
  useEffect(() => {
    if (correcting || !reasonsKnown || !defaults) return;
    setReason((current) =>
      current === '' && !editsRow
        ? defaults[sign === 'none' ? defaultDirection : sign]
        : reasonForSign(current, sign, defaults),
    );
  }, [sign, defaults, defaultDirection, correcting, reasonsKnown, editsRow]);

  const input: MovementFormInput = {
    lot,
    lotExpiry: effectiveExpiry,
    quantity,
    previousQuantity: previous ?? 0,
    previousKnown: !previousPending,
    reason,
    comment,
    lotIsNew,
    medicineActive: picked?.active ?? true,
    registrarKind: registrarKind ?? null,
  };
  const errors = submitted ? validateMovement(input) : {};
  // FR-014: said, never blocking. A correction restates a past level, which is
  // neither a purchase nor a new lot, so neither warning speaks for it.
  const warnings = picked && !correcting ? movementWarnings(input, todayIso()) : {};
  // Once the field holds the lot's own date, the row's EXPIRY_MISMATCH no longer
  // speaks for it; the replaced-date warning does.
  const rowMessages = editing
    ? rowFieldMessages(
        t,
        expiryLocked && knownLot?.expiry ? withoutIssue(editing, 'EXPIRY_MISMATCH') : editing,
      )
    : {};

  // The same medicine, lot and reason as another row of the report (FR-016).
  const duplicate =
    picked && reason !== '' && lotKey !== ''
      ? otherRows.find(
          (row) =>
            row.medicineCode === picked.code &&
            (row.lot ?? '').trim() === lotKey &&
            row.reason === reason,
        )
      : undefined;

  function fieldMessage(field: MovementField): string | undefined {
    return message(t, errors[field]) ?? (touched.has(field) ? undefined : rowMessages[field]);
  }

  // An edited row opens on its first message (FR-015), else on its quantity.
  useEffect(() => {
    if (!open || !editable) return undefined;
    const fields = Object.keys(rowFieldMessages(t, editable)) as MovementField[];
    const first = (['lot', 'lotExpiry', 'quantity', 'reason', 'comment'] as const).find((field) =>
      fields.includes(field),
    );
    return focusSoon(
      () => fieldElement(first ?? 'quantity'),
      () => rootRef.current,
    );
    // Once, as the dialog opens on the row.
  }, []);

  function resetRow(): void {
    setLot('');
    setLotExpiry('');
    setLotChosen(false);
    setLotNewChosen(false);
    setQuantity(null);
    setComment('');
    // No reason before the type that decides it is known: the default of a type
    // not read yet would outlive its arrival (§Decision 8).
    setReason(reasonsKnown && defaults ? defaults[defaultDirection] : '');
    setSubmitted(false);
  }

  function pick(hit: ReportSearchHit, chosenLot?: MatchedLot): void {
    // Found through its lots, a medicine is that lot's: an inactive one too,
    // since UC50-01 refuses only the lots new here. A lot row picked from the
    // list (T106) is the lot, whatever else matched.
    const viaLots = chosenLot ? [chosenLot] : hit.matched.includes('lot') ? hit.lots.matched : [];
    if (!hit.active && viaLots.length === 0) {
      setRefused(hit);
      return;
    }
    setRefused(null);
    resetRow();
    setPicked(hit);
    setPickedFromRow(false);
    touch('medicine');
    // Searched by the number of one lot: that lot is the one being moved.
    const only = viaLots.length === 1 ? viaLots[0] : undefined;
    if (only) {
      setLot(only.lot);
      setLotExpiry(only.expiry ?? '');
      setLotChosen(true);
    }
    setTimeout(() => focusFieldSoon(only ? 'quantity' : 'lot'), 0);
  }

  function changeMedicine(): void {
    resetRow();
    setPicked(null);
    setPickedFromRow(false);
    touch('medicine', 'lot', 'lotExpiry', 'quantity', 'reason', 'comment');
  }

  function chooseLot(chosen: ReportedLot): void {
    setLot(chosen.lot);
    setLotExpiry(chosen.expiry ?? '');
    setLotChosen(true);
    setLotNewChosen(false);
    touch('lot', 'lotExpiry');
    setTimeout(() => focusFieldSoon('quantity'), 0);
  }

  function chooseNewLot(): void {
    setLotNewChosen(true);
    const next = lotKey === '' ? 'lot' : expiryLocked ? 'quantity' : 'lotExpiry';
    setTimeout(() => focusFieldSoon(next), 0);
  }

  // "scrie alt lot": the same medicine, another lot to type (useRaportNou `altLot`).
  function unlockLot(): void {
    setLot('');
    setLotExpiry('');
    setLotChosen(false);
    setLotNewChosen(false);
    touch('lot', 'lotExpiry');
    setTimeout(() => focusFieldSoon('lot'), 0);
  }

  function fieldElement(field: MovementField): unknown {
    const targets: Record<MovementField, () => unknown> = {
      medicine: () => changeRef.current?.querySelector('mud-button, button'),
      lot: () => lotRef.current,
      lotExpiry: () => expiryRef.current?.querySelector('mud-text-input, input'),
      quantity: () => quantityRef.current,
      reason: () => reasonRef.current,
      comment: () => commentRef.current,
    };
    return targets[field]();
  }

  function submit(): void {
    if (!picked || windowClosed || !reasonsKnown || targetPending) return;
    const found = validateMovement(input);
    const first = firstInvalidField(found);
    const unconfirmed = fromApi && !apiConfirmed;
    if (first || reason === '' || quantity === null || unconfirmed) {
      // FR-015: every message on its field, and the cursor on the first one.
      // The warning sits above the fields, so its confirmation comes first.
      setSubmitted(true);
      if (unconfirmed) setTimeout(() => focusControl(apiConfirmRef.current), 0);
      else if (first) setTimeout(() => focusFieldSoon(first), 0);
      return;
    }
    if (duplicate) {
      setSubmitted(true);
      return;
    }
    const note = comment.trim();
    // The catalogue's name for a row that had only its code (T099).
    const medicineLabel = shownMedicine?.medicineLabel || picked.medicineLabel;
    const row: DraftRow = {
      ...(editing
        ? withoutComment(editing)
        : { clientRowId: newClientRowId(), validationErrors: [] }),
      medicineCode: picked.code,
      medicineLabel,
      lot: lotKey,
      lotExpiry: effectiveExpiry,
      currentQuantity: quantity,
      reason,
      // Whether the lot is new is a location's answer (R2): none yet, none given.
      lotMatch: unbound ? 'UNKNOWN' : lotIsNew ? 'NEW' : 'EXISTING',
      // An unknown previous stock is left out, so the table shows it as unknown
      // rather than as 0 (constitution art. IV); the server fills it in once the
      // report is bound to a location.
      ...(previousPending ? {} : { previousQuantity: previous ?? 0 }),
      validationErrors: [],
      // The target the correction names (UC50-05); an edited correction row
      // keeps its own through `editing`.
      ...(correction ? { correctionOf: correction.id } : {}),
      ...(note && !correcting ? { comment: note } : {}),
    };
    onAdd({ row, medicineLabel, lot: lotKey, reason, sign });
  }

  // FR-009: Enter adds, except in the comment, where it is a new line, and in
  // an open reason list, where it chooses. An Enter on a button is that button's.
  function onFormKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Enter') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (reasonListOpen.current) return;
    const origin = event.nativeEvent.composedPath()[0];
    if (origin instanceof Element && origin.closest('textarea, mud-textarea, button, mud-button'))
      return;
    event.preventDefault();
    submit();
  }

  // The strip's first cell (T109): the level the movement starts from, or in
  // correction mode the level the movement reported (R9). "—" while unknown.
  const previousLabel = t(
    correcting ? 'stock_reporting.dialog.field.reported' : 'stock_reporting.dialog.field.previous',
  );
  const previousText = previous === null ? EMPTY_CELL : formatNumber(previous);
  const previousCaption =
    !correcting && previous === 0 && lotIsNew
      ? t('stock_reporting.dialog.hint.previous_new')
      : undefined;

  // With no location, both read-only cells say "—" and one note under the
  // strip says why, rather than a long hint under each (T109).
  let differenceText = EMPTY_CELL;
  let differenceHint: string | undefined = previousPending
    ? undefined
    : t('stock_reporting.dialog.hint.difference_pending');
  if (diff !== null) {
    differenceText = diff > 0 ? `+${formatNumber(diff)}` : formatNumber(diff);
    differenceHint =
      diff > 0
        ? t('stock_reporting.dialog.hint.stock_up')
        : diff < 0
          ? t('stock_reporting.dialog.hint.stock_down')
          : t('stock_reporting.movement.error.no_difference');
  }

  const quantityLabel = t(
    correcting
      ? 'stock_reporting.dialog.field.quantity_corrected'
      : 'stock_reporting.dialog.field.quantity',
  );

  const commentRequired = reason !== '' && commentRequiredFor(reason);
  const commentHint =
    reason !== '' && commentRequired
      ? t('stock_reporting.dialog.hint.comment_required', { reason: t(reasonLabelKey(reason)) })
      : t('stock_reporting.dialog.hint.comment_optional');

  // While the type is not known the list holds nothing (§Decision 8).
  const reasonOptions = (
    correcting ? [CORRECTION_REASON] : reasonsKnown ? reasonsFor(sign, registrarKind) : []
  ).map((option) => ({
    value: option,
    label: t(reasonLabelKey(option)),
  }));

  // The (i) beside Motiv says which reasons the list holds, as the sign and the
  // Registrar's type decide, naming them from the list itself so it never names
  // one the type does not offer (piese.tsx `CampMotiv`); in correction mode, why
  // it is locked; before the type is known, that it is being read.
  const reasonsNamed = {
    reasons: reasonOptions.map((option) => option.label.toLocaleLowerCase()).join(', '),
  };
  const reasonTip = correcting
    ? t('stock_reporting.dialog.tip.reason.correction')
    : registrarKindState === 'error'
      ? t('stock_reporting.dialog.reasons_unavailable.title')
      : !reasonsKnown
        ? t('stock_reporting.dialog.hint.reasons_loading')
        : previousPending
          ? t('stock_reporting.dialog.tip.reason.all', reasonsNamed)
          : diff === null
            ? t('stock_reporting.dialog.tip.reason.pending')
            : diff === 0
              ? t('stock_reporting.dialog.tip.reason.all', reasonsNamed)
              : diff > 0
                ? t('stock_reporting.dialog.tip.reason.increase', reasonsNamed)
                : t('stock_reporting.dialog.tip.reason.decrease', reasonsNamed);

  // "lot existent" or "lot nou" beside Număr lot, once the previous stock says
  // which (piese.tsx `CampLot`, T100); a correction's lot is the movement's own.
  let lotTag: ReactNode = null;
  if (!correcting && lotKey !== '' && previous !== null) {
    lotTag = lotIsNew ? (
      <MudTag
        variant="status"
        size="sm"
        semantic="brand"
        label={t('stock_reporting.table.tag.new_lot')}
      />
    ) : (
      <MudTag
        variant="status"
        size="sm"
        semantic="neutral"
        label={t('stock_reporting.dialog.tag.existing_lot')}
      />
    );
  }

  // The lot list (FR-010): "Lot nou" first, then the lots reported, filtered by
  // what is typed. Shut once a lot is chosen or "Lot nou" was.
  const lotPanel = picked !== null && reportedLots.length > 0 && !lotChosen && !lotNewChosen;
  const typedFold = fold(lotKey);
  const lotGroups = lotPanel
    ? [
        {
          key: 'lots',
          options: typedFold
            ? reportedLots.filter((reported) => fold(reported.lot).includes(typedFold))
            : reportedLots,
        },
      ]
    : [];

  const lotError = fieldMessage('lot');
  const expiryError = fieldMessage('lotExpiry');
  const quantityError = fieldMessage('quantity');
  const reasonError = fieldMessage('reason');
  const commentError = fieldMessage('comment');
  const medicineError = fieldMessage('medicine');
  const quantityWarning = message(t, warnings.quantity);
  const expiryWarning = message(t, warnings.lotExpiry);
  // Here, at another location the caller reaches, or before a location is
  // chosen: what was taken over differs (CF-43 bounds the list to the reach).
  const lotNotice =
    knownTyped && knownLot
      ? t(
          unbound
            ? 'stock_reporting.dialog.lot.existing_unbound'
            : knownLot.reportedHere
              ? 'stock_reporting.dialog.lot.existing_here'
              : 'stock_reporting.dialog.lot.existing_elsewhere',
          { lot: lotKey },
        )
      : undefined;
  const expiryReplacedNotice = replacedExpiry
    ? t('stock_reporting.dialog.hint.expiry_replaced', {
        // Shown as the grid will show it: a typed month is `YYYY-MM`, a month by its
        // shape, and a day the row already held is a full date.
        entered: formatExpiry(replacedExpiry.entered),
        known: formatExpiry(replacedExpiry.known, replacedExpiry.knownPrecision),
        lot: lotKey,
      })
    : undefined;
  // MUD's inline message is not a live region; this one says both aloud when
  // they appear, and takes no room (global.css `.visually-hidden`).
  const lotAnnouncement = [lotNotice, expiryReplacedNotice].filter(Boolean).join(' ');

  const title = t(
    editing
      ? 'stock_reporting.dialog.title.edit'
      : correction
        ? 'stock_reporting.dialog.title.correction'
        : 'stock_reporting.dialog.title.add',
  );
  const searchTip = t('stock_reporting.dialog.tip.search');

  return (
    <MudModal
      open={open}
      size="lg"
      titleText={title}
      closeLabel={t('common.close')}
      // FR-001: a click outside does not close it; Esc and "Renunță" do.
      closeOnBackdrop={false}
      // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — the reason
      // select inside emits `mudClose` bubbling+composed; the guard answers only a real
      // dismissal of this modal.
      onMudClose={onModalDismiss(() => onClose())}
    >
      {/* T106: MUD's search field has no label row, so the search's (i) tip
          sits beside the title while the dialog searches. AGE's title slot
          replaces the default heading, so the heading is drawn here. */}
      {stepA ? (
        <span slot="title" className="simsm-movement-dialog__title">
          <h2 className="simsm-movement-dialog__title-text">{title}</h2>
          <MudTooltip content={searchTip} position="bottom">
            <span
              slot="trigger"
              className="simsm-movement-dialog__field-info"
              role="img"
              aria-label={searchTip}
            >
              <MudIcon name="circle-info" variant="filled" size={16} />
            </span>
          </MudTooltip>
        </span>
      ) : null}
      <div ref={rootRef} className="simsm-movement-dialog">
        {picked === null ? (
          <div ref={searchBoxRef} className="simsm-movement-dialog__step-a">
            {/* MUD's search pattern (decision D4, T106): the Large search
                input, its results a menu in the body under it. The field's
                name is its placeholder, so what is seen is what is heard; the
                long "any combination" guidance is the (i) beside the title. */}
            <DialogCombobox<SearchOption>
              className="simsm-movement-dialog__search"
              field="search"
              inline
              size="lg"
              loading={listing && search.isFetching}
              value={query}
              onValueChange={(value) => {
                setQuery(value);
                setRefused(null);
              }}
              groups={searchGroups}
              optionKey={(option) => (option.kind === 'lot' ? `lot:${option.lot.lot}` : 'medicine')}
              renderOption={(option) =>
                option.kind === 'lot' ? (
                  <MatchedLotOption lot={option.lot} terms={terms} />
                ) : (
                  <SearchHitOption hit={option.hit} terms={terms} />
                )
              }
              optionLabel={(option) => searchOptionLabel(t, option)}
              onSelect={(option) =>
                option.kind === 'lot' ? pick(option.hit, option.lot) : pick(option.hit)
              }
              listboxLabel={t('stock_reporting.dialog.search.listbox')}
              status={status}
              footer={footer}
              ariaLabel={t('stock_reporting.dialog.search.label')}
              required
              placeholder={t('stock_reporting.dialog.search.label')}
              inputRef={searchRef}
            />
            {/* UC50-01: never a toast here, which the dialog's top layer would cover (R12). */}
            {refused ? (
              <MudInlineMessage variant="error" size="small">
                {t('stock_reporting.dialog.search.inactive_note')}
              </MudInlineMessage>
            ) : null}
          </div>
        ) : (
          <div className="simsm-movement-dialog__form" onKeyDown={onFormKeyDown}>
            <span className="visually-hidden" role="status">
              {lotAnnouncement}
            </span>
            {/* 1. Medicament. A row's own medicine says nothing of its statuses
                or its authorisation until the catalogue has answered (T099). */}
            <MedicineSummary
              hit={shownMedicine ?? picked}
              known={!(pickedFromRow && !resolved)}
              error={medicineError}
              action={
                // A correction is the movement's medicine (R9): nothing to change.
                correcting ? null : (
                  <div ref={changeRef} className="simsm-movement-medicine__change">
                    <MudButton
                      shape="circular"
                      appearance="text"
                      variant="strict"
                      size="sm"
                      onClick={changeMedicine}
                    >
                      {t('stock_reporting.dialog.medicine.change')}
                    </MudButton>
                  </div>
                )
              }
            />

            {correcting && target ? (
              <div className="simsm-movement-dialog__correction-target">
                <CorrectionTargetLine movement={target} />
                {fromApi ? (
                  <MudInfoBox
                    emphasis="strong"
                    variant="warning"
                    titleText={t('stock_reporting.dialog.correction.api.title')}
                  >
                    <span className="simsm-movement-dialog__api-warning">
                      <span>{t('stock_reporting.dialog.correction.api.body')}</span>
                      <MudCheckbox
                        ref={apiConfirmRef}
                        size="md"
                        checked={apiConfirmed}
                        invalid={submitted && !apiConfirmed}
                        {...(submitted && !apiConfirmed
                          ? { errorText: t('stock_reporting.dialog.correction.api.required') }
                          : {})}
                        onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                          setApiConfirmed(e.detail.checked)
                        }
                      >
                        <span slot="label">
                          {t('stock_reporting.dialog.correction.api.confirm')}
                        </span>
                      </MudCheckbox>
                    </span>
                  </MudInfoBox>
                ) : null}
              </div>
            ) : null}

            {/* 2. Lot: its number and its expiry, side by side. */}
            <div className="simsm-movement-dialog__group simsm-movement-dialog__group--lot">
              <div className="simsm-movement-dialog__lot">
                <FieldHead
                  label={t('stock_reporting.dialog.field.lot')}
                  tip={t(
                    unbound
                      ? 'stock_reporting.dialog.tip.lot_no_location'
                      : 'stock_reporting.dialog.tip.lot',
                  )}
                  required
                  tag={lotTag}
                />
                <DialogCombobox<ReportedLot>
                  value={lot}
                  onValueChange={(value) => {
                    setLot(value);
                    touch('lot');
                    if (value.trim() === '') setLotNewChosen(false);
                  }}
                  groups={lotGroups}
                  optionKey={(reported) => reported.lot}
                  renderOption={(reported) => <LotOption lot={reported} unbound={unbound} />}
                  onSelect={chooseLot}
                  special={
                    lotPanel
                      ? {
                          key: 'new-lot',
                          onSelect: chooseNewLot,
                          content: (
                            <span className="simsm-movement-lot__new">
                              <span>{t('stock_reporting.dialog.lot.new')}</span>
                              <span className="simsm-movement-lot__new-hint">
                                {lotKey !== '' && !knownLot
                                  ? t('stock_reporting.dialog.lot.new_hint_typed', { lot: lotKey })
                                  : t('stock_reporting.dialog.lot.new_hint')}
                              </span>
                            </span>
                          ),
                        }
                      : undefined
                  }
                  header={
                    lotPanel ? (
                      <span className="simsm-movement-lot__row">
                        <span>
                          {t('stock_reporting.dialog.lot.header', { count: reportedLots.length })}
                        </span>
                        <span className="simsm-movement-lot__num">
                          {t('stock_reporting.dialog.lot.column.stock')}
                        </span>
                        <span className="simsm-movement-lot__num">
                          {t('stock_reporting.dialog.lot.column.expiry')}
                        </span>
                      </span>
                    ) : undefined
                  }
                  listboxLabel={t('stock_reporting.dialog.lot.listbox')}
                  ariaLabel={t('stock_reporting.dialog.field.lot')}
                  required
                  placeholder={t('stock_reporting.dialog.field.lot_placeholder')}
                  readonly={lotChosen}
                  errorText={lotError}
                  size="md"
                  inputRef={lotRef}
                />
                {lotChosen ? (
                  <span className="simsm-movement-dialog__lock">
                    <span className="simsm-movement-dialog__hint">
                      {t('stock_reporting.dialog.hint.lot_locked')}
                    </span>
                    {/* A correction may not change the lot (CORRECTION_MISMATCH). */}
                    {correcting ? null : (
                      <MudButton
                        shape="circular"
                        appearance="text"
                        variant="strict"
                        size="sm"
                        onClick={unlockLot}
                      >
                        {t('stock_reporting.dialog.action.unlock_lot')}
                      </MudButton>
                    )}
                  </span>
                ) : null}
                {lotNotice ? (
                  <MudInlineMessage variant="info" size="small">
                    {lotNotice}
                  </MudInlineMessage>
                ) : null}
              </div>
              <div ref={expiryRef} className="simsm-movement-dialog__expiry">
                {/* spec 041: month and year, sent as YYYY-MM (stored MONTH). Re-keyed on
                    locking, so a month typed before the lot was known gives way to the lot's. */}
                <FieldHead
                  label={t('stock_reporting.dialog.field.expiry')}
                  tip={t('stock_reporting.dialog.tip.expiry')}
                  required
                />
                <ExpiryMonthInput
                  key={expiryLocked ? `locked:${effectiveExpiry}` : 'free'}
                  size="md"
                  value={effectiveExpiry}
                  // A typed month is sent as the month (`YYYY-MM`), not as its last day:
                  // core stores the precision it was reported with, and the screens show
                  // it by that precision (D6).
                  emits="month"
                  onChange={(value) => {
                    setLotExpiry(value);
                    touch('lotExpiry');
                  }}
                  ariaLabel={t('stock_reporting.dialog.field.expiry')}
                  required
                  placeholder={t('expiry.month_placeholder')}
                  readonly={expiryLocked}
                  errorText={expiryError}
                />
                {/* Beside the field rather than as its helper text, which AGE clamps to
                    two lines: the sentence is the reason, and it is said whole (FR-012). */}
                {/* A month typed before the lot that the lot's replaced: said with both
                    dates, in place of the lock hint, which says the same in fewer words. */}
                {expiryReplacedNotice && expiryError === undefined ? (
                  <MudInlineMessage variant="warning" size="small">
                    {expiryReplacedNotice}
                  </MudInlineMessage>
                ) : expiryLocked && expiryError === undefined ? (
                  <span className="simsm-movement-dialog__hint">
                    {t('stock_reporting.dialog.hint.expiry_locked')}
                  </span>
                ) : null}
                {expiryWarning ? (
                  <MudInlineMessage variant="warning" size="small">
                    {expiryWarning}
                  </MudInlineMessage>
                ) : null}
              </div>
            </div>

            {/* 3. Cantitate: the MUD control owns the hierarchy. Previous stock
                is its compact support line; difference is the static result at
                the trailing edge, not a third control-like column (owner
                decision 2026-09-15). */}
            <div className="simsm-movement-dialog__group simsm-movement-dialog__quantity">
              <div className="simsm-movement-dialog__quantity-entry">
                <div className="simsm-movement-dialog__field simsm-movement-dialog__quantity-control">
                  {/* No (i) in correction mode, as the wireframe draws it. */}
                  <FieldHead
                    label={quantityLabel}
                    tip={correcting ? undefined : t('stock_reporting.dialog.tip.quantity')}
                    required
                  />
                  <MudNumericInput
                    ref={quantityRef}
                    size="md"
                    required
                    min={0}
                    step={1}
                    aria-label={quantityLabel}
                    invalid={quantityError !== undefined}
                    {...(quantity === null ? {} : { value: quantity })}
                    {...(quantityError === undefined ? {} : { errorText: quantityError })}
                    onMudInput={(event: CustomEvent<{ value: number | null }>) => {
                      setQuantity(event.detail.value ?? null);
                      touch('quantity');
                    }}
                  />
                  <MetadataLine
                    items={[
                      {
                        label: previousLabel,
                        value: (
                          <>
                            <span className="simsm-movement-dialog__previous-value">
                              {previousText}
                            </span>
                            {previousCaption ? (
                              <span className="simsm-movement-dialog__quantity-note">
                                {' '}
                                {previousCaption}
                              </span>
                            ) : null}
                          </>
                        ),
                      },
                    ]}
                  />
                </div>
                <dl className="simsm-movement-dialog__difference">
                  <dt className="simsm-movement-dialog__field-label">
                    {t('stock_reporting.dialog.field.difference')}
                  </dt>
                  <dd>
                    <span
                      className={`simsm-movement-dialog__difference-value simsm-movement-dialog__difference-value--${sign}`}
                      aria-live="polite"
                      aria-label={
                        differenceHint ? `${differenceText}. ${differenceHint}` : differenceText
                      }
                    >
                      {differenceText}
                    </span>
                    {diff === 0 && differenceHint ? (
                      <span className="simsm-movement-dialog__quantity-note">{differenceHint}</span>
                    ) : null}
                  </dd>
                </dl>
              </div>
              {previousPending ? (
                <span className="simsm-movement-dialog__hint">
                  {t('stock_reporting.dialog.hint.needs_location')}
                </span>
              ) : null}
              {quantityWarning ? (
                <MudInlineMessage variant="warning" size="small">
                  {quantityWarning}
                </MudInlineMessage>
              ) : null}
            </div>

            {/* 4. Motiv, and Comentariu under it: whether the comment is
                required follows from the reason, so the two sit together. */}
            <div className="simsm-movement-dialog__group simsm-movement-dialog__group--reason">
              <div className="simsm-movement-dialog__field">
                <FieldHead
                  label={t('stock_reporting.dialog.field.reason')}
                  tip={reasonTip}
                  required
                />
                <MudSelect
                  ref={reasonRef}
                  size="medium"
                  required
                  aria-label={t('stock_reporting.dialog.field.reason')}
                  value={reason}
                  // Locked to Corectare in correction mode, and shut until the
                  // Registrar's type is known. `disabled`, not `readonly`: the
                  // switch to MUD 1.2 keeps the behaviour it had on AGE.
                  disabled={correcting || !reasonsKnown}
                  invalid={reasonError !== undefined}
                  {...(reasonError === undefined ? {} : { errorText: reasonError })}
                  {...(registrarKindState === 'loading' && !correcting
                    ? { helperText: t('stock_reporting.dialog.hint.reasons_loading') }
                    : {})}
                  onMudChange={(event: CustomEvent<{ value: string }>) => {
                    const next = event.detail.value;
                    setReason(reasonOf(next));
                    touch('reason');
                  }}
                  onMudOpen={() => {
                    reasonListOpen.current = true;
                  }}
                  onMudClose={() => {
                    reasonListOpen.current = false;
                  }}
                >
                  {reasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </MudSelect>
              </div>
              <div className="simsm-movement-dialog__field">
                <FieldHead
                  label={t('stock_reporting.dialog.field.comment')}
                  tip={t('stock_reporting.dialog.tip.comment')}
                  required={commentRequired}
                />
                <MudTextarea
                  ref={commentRef}
                  size="md"
                  rows={2}
                  resize="vertical"
                  required={commentRequired}
                  // CF-59: read-only in correction mode, as the movement's other fields.
                  disabled={correcting}
                  aria-label={t('stock_reporting.dialog.field.comment')}
                  placeholder={t('stock_reporting.dialog.field.comment_placeholder')}
                  helperText={commentHint}
                  value={comment}
                  invalid={commentError !== undefined}
                  {...(commentError === undefined ? {} : { errorText: commentError })}
                  onMudInput={(event: CustomEvent<{ value: string }>) => {
                    setComment(event.detail.value);
                    touch('comment');
                  }}
                />
              </div>
            </div>

            {/* §Decision 8: the type deciding the reasons could not be read, said
                inside the dialog, where Motiv stays empty. */}
            {registrarKindState === 'error' && !correcting ? (
              <MudInfoBox
                emphasis="strong"
                role="alert"
                variant="error"
                titleText={t('stock_reporting.dialog.reasons_unavailable.title')}
              >
                {t('stock_reporting.dialog.reasons_unavailable.body')}
              </MudInfoBox>
            ) : null}

            {/* UC50-06, contracts/ui-flow.md: the window, said inside the dialog
                (a toast would paint under it, R12), before anything is typed. */}
            {windowClosed && correction ? (
              <MudInfoBox
                emphasis="strong"
                role="alert"
                variant="error"
                titleText={t('stock_reporting.dialog.correction.window.title')}
              >
                {t('stock_reporting.dialog.correction.window.body', {
                  date: formatDate(correction.confirmedAt),
                  days: correction.windowDays,
                })}
              </MudInfoBox>
            ) : null}

            {/* FR-016, piese.tsx `DupBox`: refused, with the way out, the row it repeats. */}
            {submitted && duplicate ? (
              <MudInfoBox
                emphasis="strong"
                role="alert"
                variant="error"
                titleText={t('stock_reporting.dialog.duplicate.title')}
              >
                {t('stock_reporting.dialog.duplicate.body')}
                <MudButton
                  slot="actions"
                  shape="circular"
                  appearance="text"
                  variant="strict"
                  size="sm"
                  onClick={() => onEditRow?.(duplicate.clientRowId)}
                >
                  {t('stock_reporting.dialog.duplicate.action')}
                </MudButton>
              </MudInfoBox>
            ) : null}
          </div>
        )}
      </div>
      <div slot="actions" className="simsm-movement-dialog__actions">
        <MudButton shape="circular" appearance="text" variant="strict" size="md" onClick={onClose}>
          {t('stock_reporting.dialog.action.cancel')}
        </MudButton>
        <MudButton
          shape="circular"
          variant="primary"
          size="md"
          // No medicine yet, or a level equal to the stock: nothing to add. The
          // reason is the difference hint beside the quantity. Any other form is
          // addable, and its errors are said on the attempt (FR-015). Nor before
          // the Registrar's type, which decides the reasons, is known (§Decision 8),
          // nor before an edited correction's movement, which may need its warning.
          disabled={picked === null || diff === 0 || windowClosed || !reasonsKnown || targetPending}
          onClick={submit}
        >
          {t(
            editing
              ? 'stock_reporting.dialog.action.save_edit'
              : correction
                ? 'stock_reporting.dialog.action.confirm_correction'
                : 'stock_reporting.dialog.action.add',
          )}
        </MudButton>
      </div>
    </MudModal>
  );
}
